import React, { useState } from 'react';
import { ATELIER, FONTS, ICON } from '../tokens';
import { useWorkspace } from '../WorkspaceContext';

/**
 * ThreadHeader - flush 60px header at the top of the thread column.
 *   ▸ project breadcrumb (project name › thread title)
 *   ▸ right-aligned actions (Search, Anchor on DAGCHAIN, Share, More)
 */
export function ThreadHeader() {
  const {
    conversation, projects, activeProjectId,
    newConversation, renameProject,
  } = useWorkspace();

  const project = projects.find(p => p.id === activeProjectId)
                || projects.find(p => p.id === conversation.projectId)
                || null;
  const hasContent = conversation.turns.length > 0;

  return (
    <div style={{
      height: 60,
      flexShrink: 0,
      borderBottom: `1px solid ${ATELIER.border}`,
      background: ATELIER.bg,
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '0 24px',
    }}>
      {/* Breadcrumb */}
      <div style={{
        flex: 1, minWidth: 0,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        {project ? (
          <ProjectCrumb project={project} onRename={(t) => renameProject(project.id, t)} />
        ) : (
          <div style={{
            fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.22em',
            fontWeight: 700, color: ATELIER.inkMuted, textTransform: 'uppercase',
          }}>
            Quick chat
          </div>
        )}

        <span style={{ color: ATELIER.inkFaint, fontSize: 14 }}>›</span>

        <div style={{
          fontSize: 14, fontWeight: 500,
          color: hasContent ? ATELIER.ink : ATELIER.inkMuted,
          letterSpacing: '-0.005em',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {hasContent ? conversation.title : 'New thread'}
        </div>
      </div>

      {/* Actions */}
      <ActionBtn
        icon="M3 7h18M3 12h18M3 17h12"
        title="New thread in this project"
        onClick={() => newConversation({ projectId: conversation.projectId })}
        accent
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d={ICON.plus} />
        </svg>
        New thread
      </ActionBtn>
    </div>
  );
}

/* ── Project breadcrumb (renamable) ─────────────────────────── */
function ProjectCrumb({ project, onRename }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(project.title);

  React.useEffect(() => { setVal(project.title); }, [project.title]);

  if (editing) {
    return (
      <input
        autoFocus
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => { setEditing(false); if (val.trim() && val !== project.title) onRename(val.trim()); }}
        onKeyDown={e => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setVal(project.title); setEditing(false); }
        }}
        style={{
          fontFamily: FONTS.serif, fontSize: 16, fontWeight: 500,
          letterSpacing: '-0.015em',
          color: ATELIER.ink,
          background: ATELIER.accentSoft,
          border: `1px solid ${ATELIER.borderAccent}`,
          borderRadius: 7,
          padding: '4px 8px',
          outline: 'none',
          minWidth: 120, maxWidth: 320,
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title="Rename project"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '4px 8px',
        background: 'transparent', border: 'none',
        cursor: 'pointer',
        borderRadius: 7,
        fontFamily: FONTS.serif, fontSize: 16, fontWeight: 500,
        letterSpacing: '-0.015em',
        color: ATELIER.ink,
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = ATELIER.bgHover; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: 2,
        background: project.color || ATELIER.accent,
        boxShadow: `0 0 6px ${project.color || ATELIER.accent}55`,
        flexShrink: 0,
      }} />
      <span style={{
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: 240,
      }}>{project.title}</span>
    </button>
  );
}

/* ── Action button (label + icon) ───────────────────────────── */
function ActionBtn({ accent, title, onClick, children }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        height: 32, padding: '0 11px',
        background: accent
          ? (hover ? ATELIER.accentSoftHover : ATELIER.accentSoft)
          : (hover ? ATELIER.bgHover : 'transparent'),
        border: `1px solid ${accent ? ATELIER.borderAccent : (hover ? ATELIER.borderStrong : 'transparent')}`,
        borderRadius: 8,
        color: accent ? ATELIER.accent : ATELIER.inkMid,
        fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
        letterSpacing: '-0.005em',
        cursor: 'pointer', whiteSpace: 'nowrap',
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
      }}
    >
      {children}
    </button>
  );
}

function IconBtn({ title, onClick, children }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 32, height: 32, borderRadius: 8,
        background: hover ? ATELIER.bgHover : 'transparent',
        border: `1px solid ${hover ? ATELIER.borderStrong : 'transparent'}`,
        cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: ATELIER.inkSoft,
        transition: 'background 0.15s, border-color 0.15s, color 0.15s',
      }}
    >
      {children}
    </button>
  );
}
